// src/helpers/firebaseServices.js
import firebase from "firebase/compat/app";
import "firebase/compat/messaging";
import "firebase/compat/analytics";
import { supportsWebPush } from "./pushSupport";

/**
 * Initialize Firebase once
 */
export const initializeFirebase = () => {
  if (firebase.apps.length) return; // prevent double init
  console.log("firebase initialized");
  firebase.initializeApp({
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    databaseURL: process.env.REACT_APP_FIREBASE_DB_URL,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
  });

  try {
    if (
      process.env.REACT_APP_FIREBASE_MEASUREMENT_ID &&
      typeof firebase.analytics === "function"
    ) {
      firebase.analytics();
    }
  } catch (e) {
    console.warn("Analytics init skipped:", e);
  }
};

/**
 * Ensure the Firebase Messaging service worker is registered AND active.
 * We explicitly bind to the FCM SW scope so push subscription goes to the right SW.
 */
async function ensureFcmSWActive() {
  if (!("serviceWorker" in navigator)) return null;

  const scope = "/firebase-cloud-messaging-push-scope";

  // Try to get existing registration for the FCM scope
  let reg = await navigator.serviceWorker.getRegistration(scope);

  // If not found, register it
  if (!reg) {
    try {
      reg = await navigator.serviceWorker.register("/firebase-messaging-sw.js", { scope });
    } catch (e) {
      console.warn("Unable to register Firebase Messaging service worker:", e);
      return null;
    }
  }

  // If active already, we're good
  if (reg.active) return reg;

  // Otherwise wait until it activates
  await new Promise((resolve) => {
    const check = () => { if (reg.active) resolve(); };
    reg.installing?.addEventListener("statechange", check);
    reg.waiting?.addEventListener("statechange", check);
    navigator.serviceWorker.ready.then(check); // backstop
  });

  return reg;
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function getTokenWithRetry(messaging, vapidKey, once = false) {
  try {
    return await messaging.getToken({ vapidKey });
  } catch (e) {
    const msg = String(e?.message || "");
    if (!once && (e?.name === "AbortError" || /no active Service Worker/i.test(msg))) {
      console.warn("getToken AbortError — retrying after SW activation…");
      await ensureFcmSWActive();
      await sleep(300);
      return getTokenWithRetry(messaging, vapidKey, true);
    }
    throw e;
  }
}

/**
 * Get a token after binding Messaging to the *FCM* service worker registration.
 * This avoids subscribing on the wrong SW (e.g., the PWA SW).
 */
async function getFcmTokenBoundToFcmSW() {
  if (!supportsWebPush()) return null;

  const reg = await ensureFcmSWActive();
  if (!reg) return null;

  const messaging = firebase.messaging();
  if (messaging.useServiceWorker) {
    // v8 compat API: bind messaging to the FCM SW registration
    messaging.useServiceWorker(reg);
  }

  const vapidKey =
    process.env.REACT_APP_FIREBASE_VAPID_KEY ||
    "BEN1x5ntXy7oyLHjHRE7p9726MrLfj_BhL86wwxy3uJaHTBE42xBATXfjk-ClaBuqDxoAFner6c-kE3JP-uAExk";

  try {
    const token = await getTokenWithRetry(messaging, vapidKey);
    console.log("FCM token obtained successfully");
    console.log("Token:", token);
    return token;
  } catch (err) {
    console.warn("getToken failed:", err?.message || err);
    return null;
  }
}

/**
 * Safe to call on page load:
 * - If permission is already 'granted', returns a token.
 * - If not granted, returns null (do NOT auto-prompt).
 */
export const askForPermissionToReceiveNotifications = async () => {
  console.log("Checking for notification permission....");
  try {
    if (!supportsWebPush()) return null;
    const current = Notification.permission;
    if (current !== "granted") return null;

    console.log("Getting FCM token...");
    return await getFcmTokenBoundToFcmSW();
  } catch (error) {
    console.error("Error in notification permission flow:", error);
    return null;
  }
};

/**
 * Call this from a BUTTON CLICK (user gesture) to request permission,
 * then get a token if granted.
 */
export const requestNotificationPermission = async () => {
  if (!supportsWebPush()) return null;

  const permission = await Notification.requestPermission();
  console.log("Permission after request:", permission);
  if (permission !== "granted") return null;

  return await getFcmTokenBoundToFcmSW();
};

/**
 * Safe to call on page load; returns a token only if permission is already granted.
 * Does NOT request permission.
 */
export const getExistingTokenIfGranted = async () => {
  try {
    if (!supportsWebPush()) return null;
    if (Notification.permission !== "granted") return null;
    return await getFcmTokenBoundToFcmSW();
  } catch (error) {
    console.warn("Could not get existing token:", error?.message || error);
    return null;
  }
};

/**
 * Proper unregister: delete the current token (no deprecated requestPermission)
 */
export const unregisterDevice = async () => {
  if (!supportsWebPush()) return;
  try {
    const messaging = firebase.messaging();
    const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
    const token = await messaging.getToken({ vapidKey });
    if (token) {
      await messaging.deleteToken(token);
      console.log("FCM token deleted");
    }
  } catch (e) {
    console.warn("unregisterDevice failed", e);
  }
};

export const getMessaging = () => firebase.messaging?.();

/**
 * Listen for service worker messages requesting token refresh
 * Call this once on app init
 */
export function attachSwRefreshListener(onToken) {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;
  
  navigator.serviceWorker.addEventListener("message", async (evt) => {
    if (evt?.data?.type === "FCM_REFRESH_TOKEN") {
      console.log("[FCM] Service worker requested token refresh");
      
      try {
        const newToken = await getExistingTokenIfGranted();
        if (newToken && typeof onToken === "function") {
          onToken(newToken);
        }
      } catch (e) {
        console.warn("[FCM] Token refresh failed:", e?.message || e);
      }
    }
  });
}

/**
 * Subscribe to messages posted by the Service Worker (SW → page bridge).
 * You will receive objects like: { type: "PUSH_CHAT", data: {...} } or { type: "FCM_REFRESH_TOKEN" }.
 * Returns an unsubscribe function.
 */
export function subscribePushBridge(onEvent) {
  if (typeof window === "undefined") return () => {};
  if (!("serviceWorker" in navigator)) return () => {};
  const handler = (evt) => {
    const msg = evt?.data;
    if (!msg || !msg.type) return;
    try { onEvent(msg); } catch {}
  };
  navigator.serviceWorker.addEventListener("message", handler);
  return () => navigator.serviceWorker.removeEventListener("message", handler);
}

/**
 * Fallback: subscribe to FCM foreground messages (some browsers route foreground messages to the page).
 * Normalizes the payload to the same shape the SW bridge sends and returns an unsubscribe function.
 */
export function subscribeForegroundFCM(onEvent) {
  try {
    const messaging = firebase.messaging();
    const unsubscribe = messaging.onMessage((payload) => {
      const n = payload?.notification || {};
      const d = payload?.data || {};
      const evt = {
        type: "PUSH_CHAT",
        data: {
          kind: d.type || "unknown",
          chatId: d.id || d.chat_id || null,
          messageId: payload.fcmMessageId || d.mid || d.message_id || String(Date.now()),
          title: n.title || d.title || "Notification",
          body:  n.body  || d.body  || "",
          url:   d.url || (typeof window !== "undefined" ? window.location.origin + "/#/updates" : ""),
          unread: d.unread ? parseInt(d.unread, 10) : null,
        },
      };
      try { onEvent(evt); } catch {}
    });
    return () => (typeof unsubscribe === "function" ? unsubscribe() : undefined);
  } catch {
    return () => {};
  }
}